import { Injectable, OnDestroy } from '@angular/core';

import { ActivatedRoute, Params } from '@angular/router';
import { Subscription } from 'rxjs';
import { IRouteKeyData } from './models/route-key-data';
import { routeKey } from './models/route-key';
import { ReloadService } from './reload.service';
import { injectRequired } from 'src/app/infrastructure/functions/inject-required.function';
import { ApplicationUserService } from '../application-user.services/application-user.service';
import { domainUserDto } from 'src/assets/common/models/domain-user-dto.g';
import { projectMemberDto } from 'src/assets/common/models/project-member-dto.g';

@Injectable()
export class ActivatedRouteService
    implements OnDestroy
{
    private readonly __paramsSubscr: Subscription;
    private readonly __queryParamsSubscr: Subscription;
    
    private readonly __route = injectRequired(ActivatedRoute);
    private readonly __reloadService = injectRequired(ReloadService);
    private readonly __applicationUserService = injectRequired(ApplicationUserService);

    params: Params | null = null;
    queryParams: Params | null = null;

    private __routeKeys: IRouteKeyData | null = null;

    /**
     * Route-afgeleide keys, aangevuld met fallback uit ApplicationUserService:
     * staan dauGuid/pamGuid niet in het pad of de query-params, dan worden ze
     * read-time afgeleid van de geladen domainUser/projectMember.
     * Een expliciete `null` (via routeKeys_set) betekent "bewust geen scope" en wint van de
     * fallback; alleen `undefined` (key niet bekend) valt terug. Zonder dat onderscheid kreeg
     * AdminPanel (geen project-scope) de pamGuid van een eerder bezocht project mee, waardoor
     * claim_check op oGuid mismatchte en ALLE admin-tabs geweigerd werden.
     */
    get routeKeys(): IRouteKeyData | null {
        const _dauFallback = this.__applicationUserService.domainUserData?.[domainUserDto.guid]   ?? null;
        const _pamFallback = this.__applicationUserService.projectMemberData?.[projectMemberDto.guid] ?? null;

        if (!this.__routeKeys && !_dauFallback && !_pamFallback) return null;

        return {
            ...this.__routeKeys,
            dauGuid: this.__routeKeys?.dauGuid !== undefined ? this.__routeKeys.dauGuid : _dauFallback,
            pamGuid: this.__routeKeys?.pamGuid !== undefined ? this.__routeKeys.pamGuid : _pamFallback,
        };
    }

    constructor() {
        this.__paramsSubscr = this.__route.params.subscribe(params => {
            if (!params) return;

            this.params = params;
            this.__routeKeys = {...this.__routeKeys, ...params};
        });
        this.__queryParamsSubscr = this.__route.queryParams.subscribe(queryParams => {
            this.queryParams = queryParams;
            this.__routeKeys = {
                ...this.__routeKeys,
                ...Object.fromEntries(
                    Object.entries(queryParams).filter(([key]) =>
                        [routeKey.dauGuid as string, routeKey.pamGuid as string].includes(key)
                    )
                )
            };
        });

        const _nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
        const _isReload = this.__reloadService.isReload$$();

        if (_nav?.type === 'reload' && !_isReload) {
            this.__reloadService.isReload$$.set(false);
        }
    }

    routeKeys_set(routeKeys: IRouteKeyData): void {
        this.__routeKeys = routeKeys;
    }

    ngOnDestroy(): void {
        this.__paramsSubscr?.unsubscribe();
        this.__queryParamsSubscr?.unsubscribe();
   }
}
